import type { ReactNode } from 'react';

/**
 * Sehr schlanke Table-Wrapper-Komponenten. Volle Kontrolle über Markup,
 * keine eingebaute Sortier-/Pagination-Logik – das übernimmt der Aufrufer
 * (TanStack Query + URL-Params). Soll später durch TanStack Table abgelöst
 * werden, falls Spalten-Konfiguration komplexer wird.
 */

interface TableProps {
  children: ReactNode;
  caption?: string;
  className?: string;
}

export function Table({ children, caption, className }: TableProps): JSX.Element {
  return (
    <div className={['overflow-x-auto rounded-lg border border-border', className ?? ''].join(' ')}>
      <table className="w-full border-collapse text-left text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        {children}
      </table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }): JSX.Element {
  return <thead className="bg-surface-2 text-xs uppercase text-muted">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }): JSX.Element {
  return <tbody className="divide-y divide-border bg-surface">{children}</tbody>;
}

export function TR({
  children,
  onClick,
  selected,
  muted,
  'data-testid': testId,
}: {
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  muted?: boolean;
  /** Optional test-id, durchgereicht ans <tr>-Element. */
  'data-testid'?: string;
}): JSX.Element {
  const isInteractive = Boolean(onClick);
  return (
    <tr
      onClick={onClick}
      data-testid={testId}
      className={[
        isInteractive ? 'cursor-pointer hover:bg-surface-2' : '',
        selected ? 'bg-primary/5' : '',
        muted ? 'opacity-60 [&_td]:border-dashed' : '',
      ].join(' ')}
    >
      {children}
    </tr>
  );
}

export function TH({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <th scope="col" className={['px-3 py-2 font-medium', className ?? ''].join(' ')}>
      {children}
    </th>
  );
}

export function TD({
  children,
  className,
  title,
  colSpan,
}: {
  children: ReactNode;
  className?: string;
  /** Native HTML title attribute (Tooltip). */
  title?: string;
  colSpan?: number;
}): JSX.Element {
  return (
    <td
      title={title}
      colSpan={colSpan}
      className={['px-3 py-2 align-middle', className ?? ''].join(' ')}
    >
      {children}
    </td>
  );
}
